/**
 * Convert a String into Title Case format.
 *
 * To Title Case © 2018 David Gouch
 *
 * @param {string} String The word(s) to convert
 *
 * @returns String
 *
 * @source https://github.com/gouch/to-title-case
 */
export const toTitleCase = (string) => {
    const smallWords = /^(a|an|and|as|at|but|by|en|for|if|in|nor|of|on|or|per|the|to|v.?|vs.?|via)$/i;
    const alphanumericPattern = /([A-Za-z0-9\u00C0-\u00FF])/;
    const wordSeparators = /([ +:–—-]|%28)/;

    return string
        .split(wordSeparators)
        .map((current, index, array) => {
            if (
                /* Check for small words */
                current.search(smallWords) > -1 &&
                /* Skip first and last word */
                index !== 0 &&
                index !== array.length - 1 &&
                /* Ignore title end and subtitle start */
                array[index - 3] !== ':' &&
                array[index + 1] !== ':' &&
                /* Ignore small words that start a hyphenated phrase */
                (array[index + 1] !== '-' || (array[index - 1] === '-' && array[index + 1] === '-'))
            ) {
                return current.toLowerCase();
            }

            /* Ignore intentional capitalization */
            if (current.substr(1).search(/[A-Z]|\../) > -1) {
                return current;
            }

            /* Ignore URLs */
            if (array[index + 1] === ':' && array[index + 2] !== '') {
                return current;
            }

            /* Capitalize the first letter */
            return current.replace(alphanumericPattern, (match) => {
                return match.toUpperCase();
            });
        })
        .join('');
};

/**
 * Lookup and return the human-friendly label for a given Facet option key.
 * e.g. "stageEarlyChildhood" would return; "Early Childhood"
 *
 * @param {String} optionKey The key to lookup the value for
 * @param {Boolean} useAbbreviation Return the shortened version of the label
 *
 * @returns String
 */
export const getFacetOptionLabelByKey = (optionKey) => {
    let optionLabel = '';

    /**
     * These values below may look a little bit redundant, but
     * it's because sometimes we get lowercase (e.g. for Facets),
     * sometimes we get query string values and sometimes the
     * original value (e.g. in result metadata).
     */

    switch (optionKey) {
        case 'typeProgram':
            optionLabel = 'This is a program result type.';
            break;

        default:
            /**
             * If there is no specific rule, then just add
             * some capitalisation to the key provided.
             */
            optionLabel = toTitleCase(optionKey);
            break;
    }

    return optionLabel;
};

/**
 * Check through a given query string and return
 * all values that match a provided key.
 *
 * @param queryString
 * The full query string (or URL).
 * e.g. id=1234&src=home&campaign-ID=r569_WW&f.Type|type=abc&f.Type|type=xyz
 * @param key
 * The key of the options to be matched.
 * e.g. f.Type|type
 *
 * @returns Array of matched values
 */
export const getQueryParameterValuesByKey = (queryString: string, key: string) => {
    const queryKeyValues: string[] = [];
    let splitQueryString: string[] = [];
    let cleanedQueryString = '';
    let allQueryParameters: string[];
    let currentParameter: RegExpMatchArray | null;
    let escapedQueryKey = '';
    let valueTest: RegExp;

    // Remove anything that's not the query parameter key/value pairs
    splitQueryString = queryString.split('?');

    if (splitQueryString.length !== 1) {
        cleanedQueryString = splitQueryString[1];
    } else {
        cleanedQueryString = splitQueryString[0];
    }

    // Split all the query values into an Array
    allQueryParameters = cleanedQueryString.split('&');

    // Escape any special characters in the query key
    escapedQueryKey = key.replace(/\./g, '\\.').replace(/\|/g, '%7C').replace(/\+/g, '\\+');

    // Assemble the RegExp
    valueTest = new RegExp(`(?=^|&)${escapedQueryKey}=(.*?)(?=&|$)`);

    // Check all the parameters for a key match and extract the value
    for (let i = 0, j = allQueryParameters.length; i < j; ++i) {
        currentParameter = allQueryParameters[i].match(valueTest);

        if (currentParameter !== null && currentParameter[1] !== '') {
            queryKeyValues.push(currentParameter[1]);
        }
    }

    return queryKeyValues;
};

/**
 * Converts an input string to kebab-case
 * @module kebabCase
 * @param {string} string - The string to be converted
 * @return {string} - The string now in kebab case
 */
export const toKebabCase = (string) => {
    return string
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .replace(/([-!$%^&*()_+|~=`{}[\]:";'<>?,./])/g, '-')
        .toLowerCase()
        .replace(/\s+/g, '-')
        .toLowerCase();
};

/**
 * Get the absolute Y (top) position of an element in the document.
 * i.e. not it's relative position to the current viewport,
 * but it's fixed position relative to the top of the document
 *
 * @returns Integer
 *
 * @source https://stackoverflow.com/questions/442404/retrieve-the-position-x-y-of-an-html-element
 */
export const getElementAbsoluteYPosition = (htmlElement) => {
    const pageElementRect = htmlElement.getBoundingClientRect();
    const currentScrollTop = document.documentElement.scrollTop ? document.documentElement.scrollTop : document.body.scrollTop;
    return pageElementRect.top + currentScrollTop;
};

/**
 * Given a String value, check if a String exceeds a specific
 * number of characters.
 * If it does, split the String at the given length and
 * append an ellipis.
 *
 * @param {String} stringValue The value to evaluate
 * @param {Integer} limit The maximum allowed characters in the value
 *
 * @returns String
 */
export const truncateString = (stringValue: string, limit: number) => {
    // Don't bother truncating if there's nothing to truncate.
    if (stringValue.length <= limit) return stringValue;

    // Regex to match the truncation up to the limit
    // Prevents words from being cut off, but trims non word characters
    const truncateRegex = new RegExp(`^(.{1,${limit}}[^\\W]{1,10}).*`, 'igm');

    // Begin truncating
    const truncatedString = stringValue.replace(truncateRegex, '$1');

    return `${truncatedString}&hellip;`;
};

/**
 * Appends the click tracking url to the funnelback url
 *
 * @param {String} path the ClickTrackingUrl path
 * @returns
 */
export const buildClickTrackingUrl = (path) => `https://dxp-au-search.funnelback.squiz.cloud${path}`;

/**
 * Sanitises the provided string.
 * Removing any script tags primarily before being rendered on the frontend.
 *
 * @param {String} string
 * @returns
 */
export const sanitiseString = (string) => {
    // Return empty string if string is null/undefined
    if (!string) return '';

    // Removes script tags and all their content to make it HTML safe
    const regex = /(<|&lt;|%3C|&#60)script[\s\S]*?(>|&gt;|%3E|&#62;)[\s\S]*?(<|&lt;|%3C|&#60)\/script(>|&gt;|%3E|&#62;)/gi;

    return string.replace(regex, '');
};

/**
 * Decodes html entities inside a string by putting it inside a DOM node and retrieving the text.
 * Sanitised first to prevent XSS.
 *
 * @param {String} string
 */
export const decodeString = (string: string) => {
    // Return empty string if string is null/undefined
    if (!string) return '';

    const sanitisedString = sanitiseString(string);
    const element = document.createElement('textarea');

    // Add innerHTML to decode HTML entities
    element.innerHTML = sanitisedString;

    return element.value;
};

/**
 * Parses the the readingTime into a readable string for the end user.
 *
 * @param {String / Integer} readingTime
 * @returns {String} readable time
 */
export function readableReadingTime(readingTime) {
    try {
        let parsedReadingTime: string | number;

        if (Array.isArray(readingTime)) {
            parsedReadingTime = Number.parseInt(readingTime[0]);
        } else {
            parsedReadingTime = Number.parseInt(readingTime);
        }

        // if no readingTime, output nothing
        if (!parsedReadingTime) {
            return '';
        }

        // If less than 60 seconds, return less than a minute read
        if (parsedReadingTime < 60) {
            return '< 1 min read';
        }

        // Calculate hours.
        const hours = Math.floor(parsedReadingTime / 3600);
        // Calculate minutes. Modulo so that we remove any hours.
        const minutes = Math.ceil((parsedReadingTime % 3600) / 60);

        // Output a different reading time string based on if there are hours and minutes
        if (hours) {
            // Rounding the minutes to 30
            if (minutes >= 15 && minutes <= 45) {
                return `${hours}.5 hr read`;
            }
            // Rounding to the hour
            if (minutes < 15) {
                return `${hours} hr read`;
            }
            // Rounding to the next hour
            if (minutes > 45) {
                return `${hours + 1} hr read`;
            }
        }

        // Return just the minute reading time
        return `${minutes} min read`;
    } catch {
        // On error return no reading
        return 'Bad Time';
    }
}
